import { asUserAuthor } from 'domain/shared/author';
import React from 'react';
import { ICompany } from 'types/company';
import UpdateForm from '../UpdateForm';
import { getOrUndefined } from 'util/resource';
import useNotify from 'hooks/useNotify';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import { userDashboardKey } from 'pages/Dashboard/useUserDashboardData';
import { invalidate } from 'hooks/useSWR';
import { companyOnboardingKey } from 'apis/OnboardingAPI';
import useLazyResource from 'util/resource/useLazyResource';
import { communityUpdateAPI, companyUpdateAPI } from 'apis/CompanyAPI/content/UpdateAPI';
import { CommunityUpdate, CompanyUpdate } from 'types/content';
import { CompanyUpdateFormValues } from '../types';
import { PostToCommunity, PostToCompany } from 'domain/Updates/shared/types';
import { EmbeddedUpdate, PostBlot } from 'ui/elements/form/RichTextEditor/components/UpdateBlot';
import { CompanyUpdateVisibility } from 'types/content/update';
import { SINGLE_LINE_BREAK } from 'ui/elements/form/RichTextEditor';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  company: ICompany;
  onUpdatePosted: (result?: CommunityUpdate | CompanyUpdate) => void;
  successMessage?: string;
  embeddedUpdate?: EmbeddedUpdate;
  defaultVisibility?: CompanyUpdateVisibility;
}

export default function PostUpdateDialog(props: Props) {
  const notify = useNotify();
  const { onClose, isOpen, successMessage, embeddedUpdate, defaultVisibility } = props;
  const { resource: userProfileResource } = useSelfUserProfile();
  const userProfile = getOrUndefined(userProfileResource);

  const author = asUserAuthor(userProfile);

  type postResult =
    | { type: 'companyUpdate'; update: CompanyUpdate }
    | { type: 'communityUpdate'; update: CommunityUpdate };

  const [createUpdate, isSaving] = useLazyResource<
    CompanyUpdateFormValues & { postTo: PostToCompany | PostToCommunity },
    postResult
  >(
    dto => {
      if (dto.postTo.type === 'company') {
        const updatesAPI = companyUpdateAPI(props.company.id);
        return updatesAPI
          .create({ ...dto, visibility: dto.postTo.visibility })
          .then(update => ({ type: 'companyUpdate', update }));
      } else {
        const api = communityUpdateAPI(dto.postTo.profile.id);
        return api
          .create({
            ...dto,
            postedBy: { type: 'company', company: props.company, companyId: props.company.id },
            visibility: 'CommunityMembers',
          })
          .then(update => ({ type: 'communityUpdate', update }));
      }
    },
    {
      onSuccess: result => {
        notify('success', successMessage ?? 'Update posted!');
        onClose();
        props.onUpdatePosted(result.update);
        invalidate(userDashboardKey);
        invalidate(companyOnboardingKey(props.company.slug));
      },
      onFailure: () => {
        notify('error', `Something went wrong when trying to post update`);
      },
    },
  );

  if (isOpen) {
    return (
      <UpdateForm
        author={author}
        selectedVisibility={defaultVisibility ?? 'Public'}
        onSave={(dto, postTo) => createUpdate({ ...dto, postTo })}
        isSaving={isSaving}
        onClose={onClose}
        company={props.company}
        contentDraft={
          embeddedUpdate ? SINGLE_LINE_BREAK + PostBlot.create(embeddedUpdate).outerHTML + SINGLE_LINE_BREAK : undefined
        }
        initialCursorPosition={embeddedUpdate ? 0 : undefined}
      />
    );
  }

  return null;
}
